import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const removeToast = useCallback((id) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, []);

    const addToast = useCallback((message, type = 'info') => {
        const id = Date.now();
        setToasts((prev) => [...prev, { id, message, type }]);

        // Auto remove after 3 seconds
        setTimeout(() => {
            removeToast(id);
        }, 3000);
    }, [removeToast]);

    const success = useCallback((message) => addToast(message, 'success'), [addToast]);
    const error = useCallback((message) => addToast(message, 'danger'), [addToast]);
    const info = useCallback((message) => addToast(message, 'info'), [addToast]);
    const warning = useCallback((message) => addToast(message, 'warning'), [addToast]);

    const contextValue = useMemo(() => ({
        success, error, info, warning
    }), [success, error, info, warning]);

    return (
        <ToastContext.Provider value={contextValue}>
            {children}
            <style>{`
                .toast-custom {
                    backdrop-filter: blur(8px);
                    border-radius: 12px;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.15);
                    min-width: 300px;
                    border-left: 5px solid transparent;
                    transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
                    animation: slideInRight 0.4s ease forwards;
                }
                @keyframes slideInRight {
                    from { transform: translateX(100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
                .toast-custom.bg-success { border-left-color: #198754; color: #ffffff; background: rgba(25, 135, 84, 0.95); }
                .toast-custom.bg-danger { border-left-color: #dc3545; color: #ffffff; background: rgba(220, 53, 69, 0.95); }
                .toast-custom.bg-warning { border-left-color: #ffc107; color: #ffffff; background: rgba(201, 138, 34, 0.95); }
                .toast-custom.bg-info { border-left-color: #0dcaf0; color: #ffffff; background: rgba(13, 110, 253, 0.95); }
            `}</style>
            <div className="toast-container position-fixed top-0 end-0 p-4 z-[2000]">
                {toasts.map((toast) => (
                    <div
                        key={toast.id}
                        className={`toast-custom show d-flex align-items-center p-3 mb-3 bg-${toast.type}`}
                        role="alert"
                    >
                        <div className="d-flex w-100 align-items-center">
                            <div className="fs-4 me-3">
                                {toast.type === 'success' && <i className="las la-check-circle"></i>}
                                {toast.type === 'danger' && <i className="las la-times-circle"></i>}
                                {toast.type === 'warning' && <i className="las la-exclamation-triangle"></i>}
                                {toast.type === 'info' && <i className="las la-info-circle"></i>}
                            </div>
                            <div className="flex-grow-1 fw-medium text-white">
                                {toast.message}
                            </div>
                            <button
                                type="button"
                                className="btn-close btn-close-white ms-2"
                                onClick={() => removeToast(toast.id)}
                            ></button>
                        </div>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};
